import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { imprimirPdf } from "@/lib/download";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { ClienteCelda, PagoCelda, Pill } from "@/components/organizador/OrgCeldas";

const sel = "h-8 w-full rounded border border-zinc-300 bg-white px-1 text-sm disabled:bg-zinc-50";

const GuiaInput = ({ r, p, edita, onSaved }) => {
  const [g, setG] = useState(p.guia);
  const [c, setC] = useState(p.costo || "");
  useEffect(() => { setG(p.guia); setC(p.costo || ""); }, [p.guia, p.costo]);
  const guardar = async () => {
    if (!p.paquete) { toast.error("Asigne primero el producto a un paquete"); return; }
    if (g.trim() === p.guia && Number(c || 0) === Number(p.costo || 0)) return;
    try { onSaved(await api.put(`/organizador/${r.id}/paquete`, { n: p.paquete, guia: g.trim(), costo: Number(c || 0) }).then((x) => x.data)); toast.success("Paquete guardado"); }
    catch (e) { toast.error(formatApiError(e)); setG(p.guia); setC(p.costo || ""); }
  };
  if (!edita) return [<td key="g" className="px-2 font-mono text-[13px]">{p.guia || "—"}</td>, <td key="c" className="px-2 text-right">{p.guia ? formatMoney(p.costo) : ""}</td>];
  return [
    <td key="g" className="px-1"><input value={g} onChange={(e) => setG(e.target.value)} onBlur={guardar} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} placeholder="Guía"
      className="h-8 w-28 rounded border border-zinc-300 px-1 font-mono text-[13px]" data-testid={`org-guia-${r.numero}-${p.idx}`} /></td>,
    <td key="c" className="px-1"><input type="number" min="0" step="0.01" value={c} onChange={(e) => setC(e.target.value)} onBlur={guardar} placeholder="Q0.00"
      className="h-8 w-20 rounded border border-zinc-300 px-1 text-right text-[13px]" data-testid={`org-costo-${r.numero}-${p.idx}`} /></td>,
  ];
};

const Detalle = ({ r, tab }) => (
  <div className="text-[11px] leading-snug space-y-1" data-testid={`org-detalle-${r.numero}`}>
    {r.empacado_at && <div><b className="text-emerald-700">Empacado</b> {r.empacado_at}<br />{r.empacado_por}</div>}
    {r.paquetes.map((p) => (
      <div key={p.guia || p.n} className={p.colocado_at ? "text-emerald-800" : "text-zinc-600"}>
        <span className="font-mono font-bold">{p.guia || `Paq ${p.n}`}</span>
        {p.colocado_at ? <> · {p.agencia} · {p.colocado_at} · {p.colocado_por}</> : tab !== "organizador" && <> · {p.estado || "sin guía"}</>}
      </div>
    ))}
  </div>
);

export const OrgPedido = ({ r, tab, abiertoCli, abiertoPago, empacadores, onSaved, onNuevoEmpacador }) => {
  const edita = tab === "organizador";
  const maxN = Math.max(0, ...r.paquetes.map((p) => p.n || 0), ...r.productos.map((p) => p.paquete || 0));
  const opciones = Array.from({ length: maxN + 1 }, (_, i) => i + 1);
  const patch = async (idx, body) => {
    try { onSaved((await api.patch(`/organizador/${r.id}/item/${idx}`, body)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const elegirEmp = (idx, v) => {
    if (v === "__nuevo") { const n = onNuevoEmpacador(); if (n) patch(idx, { empacador: n }); return; }
    patch(idx, { empacador: v });
  };
  const n = r.productos.length || 1;
  const cerrado = r.completo && edita;
  return r.productos.map((p, k) => (
    <tr key={p.idx} className={`border-b border-zinc-200 align-middle ${k === n - 1 ? "border-b-4 border-b-zinc-100" : ""}`} data-testid={`org-fila-${r.numero}-${p.idx}`}>
      {k === 0 && (
        <>
          <td rowSpan={n} className="px-2 py-2 text-center bg-amber-50/70 border-l-4 border-l-[#F59E0B] align-middle">
            <div className="font-black text-lg whitespace-nowrap" data-testid={`org-pedido-${r.numero}`}>{r.numero}</div>
            <button onClick={() => imprimirPdf(`/organizador/${r.id}/resumen.pdf`).catch((e) => toast.error(String(e.message || e)))}
              className="mt-1 inline-flex items-center justify-center w-12 h-9 rounded-md border-2 border-zinc-800 bg-white hover:bg-zinc-100" title="Imprimir resumen" data-testid={`org-imprimir-${r.numero}`}>
              <Printer className="w-5 h-5" />
            </button>
            {cerrado && (
              <button onClick={async () => { try { onSaved((await api.post(`/organizador/${r.id}/confirmar`)).data); } catch (e) { toast.error(formatApiError(e)); } }}
                className="mt-1 block w-full text-[10px] font-black uppercase bg-emerald-600 text-white rounded px-1 py-1" data-testid={`org-confirmar-${r.numero}`}>Confirmar</button>
            )}
          </td>
          <td rowSpan={n} className="px-2 py-2 bg-emerald-50/60 min-w-[220px] max-w-[260px]"><ClienteCelda r={r} abierto={abiertoCli} /></td>
          <td rowSpan={n} className="px-2 py-2 bg-amber-50/60 min-w-[170px]"><PagoCelda r={r} abierto={abiertoPago} /></td>
        </>
      )}
      <td className="px-2 py-1.5 text-[13px] font-semibold min-w-[120px]">{p.producto}</td>
      <td className="px-2 py-1.5 text-[12px] leading-tight min-w-[120px]">{p.caracteristicas.map((c) => <div key={c}>{c}</div>)}</td>
      {k === 0 && <td rowSpan={n} className="px-2 py-1.5 text-[12px] whitespace-pre-line min-w-[150px] max-w-[240px]">{r.notas || "—"}</td>}
      <td className="px-2 text-center font-bold" data-testid={`org-cant-${r.numero}-${p.idx}`}>{p.cantidad}{p.escaneados > 0 && p.escaneados < p.cantidad ? <div className="text-[10px] text-amber-700">{p.escaneados} esc.</div> : null}</td>
      <td className="px-1 w-16">
        <select value={p.paquete || ""} disabled={!edita} onChange={(e) => patch(p.idx, { paquete: Number(e.target.value) || 0 })} className={sel} data-testid={`org-paquete-${r.numero}-${p.idx}`}>
          <option value="">—</option>
          {opciones.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </td>
      <GuiaInput key={`${p.paquete}`} r={r} p={p} edita={edita} onSaved={onSaved} />
      <td className="px-1 min-w-[110px]">
        <select value={p.empacador} disabled={!edita} onChange={(e) => elegirEmp(p.idx, e.target.value)} className={sel} data-testid={`org-empacador-${r.numero}-${p.idx}`}>
          <option value="">—</option>
          {[...new Set([...empacadores, p.empacador].filter(Boolean))].map((x) => <option key={x} value={x}>{x}</option>)}
          <option value="__nuevo">+ Nuevo…</option>
        </select>
      </td>
      <td className="px-1 min-w-[120px]"><Pill tipo="fab" k={p.fab_estado} lbl={p.fab_lbl} id={`org-fab-${r.numero}-${p.idx}`} /></td>
      <td className="px-1 min-w-[120px]"><Pill tipo="emp" k={p.emp_estado} lbl={p.emp_lbl} id={`org-emp-${r.numero}-${p.idx}`} /></td>
      {tab !== "organizador" && k === 0 && <td rowSpan={n} className="px-2 py-2 min-w-[180px]"><Detalle r={r} tab={tab} /></td>}
    </tr>
  ));
};
